import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { ProjectsEditorService, ProjectsService } from '@app/services/app/projects.service';
import { AnnotationsService } from '@core/services/annotations/annotations.service';
import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';

import * as flContexts from '@core/constants/files-list-contexts.constants';
import * as Objects from '@core/objects';

@Component({
    selector: 'project-details-messages-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss']
})
export class ProjectDetailsMessagesListComponent implements OnInit, OnDestroy{
    public readonly ACCESS_WRITE = "access_request_write_comments";

	@Input() project: Objects.Request = null;
    @Input() user: Objects.User = null;

    protected subscriptor: Subscription;
    protected accessSubscriptor: Subscription;

    public messages: Array<Objects.Message> = null;
    public prefixUrl: string = "";
    public accessWrite: boolean = false;
    public attachmentsOpenMap: { [messageId: string]: boolean } = {};
    public notesCounterMap: { [messageId: number]: Objects.AnnotationsCounter[] } = {};
    public loadingNotesMap: { [messageId: number]: boolean } = {};
    public scope: string = flContexts.CTX.MESSAGE_DEFAULT;

    public fileMenu: Array<ThreeDotsMenuItem> = [
        {
            title: "Download File",
            key: "download",
            icon: "download"
        }
    ];
     
	constructor(
        private projectsSrv: ProjectsService,
        private editorSrv: ProjectsEditorService,
        private annotationsSrv: AnnotationsService,
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService,
        private cdr: ChangeDetectorRef
    ){}

    ngOnInit() {
        this.accessSubscriptor = this.editorSrv.selectAccess(this.ACCESS_WRITE).subscribe((access: boolean) => this.accessWrite = access);
        this.subscriptor = this.projectsSrv.selectProjectMessages().subscribe((messages: Objects.Message[]) => {
            this.messages = messages != null ? messages.filter((message: Objects.Message) => message.text.indexOf("(TWONA") != 0) : messages;
            this.messages?.forEach(message => this.loadNotesForMessage(message));
        });

        this.projectsSrv.loadMessages();
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes.project) 
            this.prefixUrl = "/requests/" + this.project.id;
    }

    ngOnDestroy() {
        this.subscriptor.unsubscribe();
        this.accessSubscriptor.unsubscribe();
    }

    public onDeleted(message: Objects.Message){
        this.projectsSrv.loadMessages();
    }

    public toggleAttachments(message: any, itemKey?: string | number): void {
        if (!itemKey && (!message || typeof message !== 'object' || !message.id)) {
            return;
        }

        const messageKey = itemKey != null ? String(itemKey) : String(message.id);
        this.attachmentsOpenMap[messageKey] = !this.attachmentsOpenMap[messageKey];
    }

    public isAttachmentsOpen(message: any, itemKey?: string | number): boolean {
        const messageKey = itemKey != null ? String(itemKey) : (message && typeof message === 'object' && message.id ? String(message.id) : '');
        return !!messageKey && !!this.attachmentsOpenMap[messageKey];
    }

    private loadNotesForMessage(message: Objects.Message): void {
        if (!message || !message.id || !(message.attachments?.length > 0 || message.files?.length > 0)) {
            return;
        }

        if (this.loadingNotesMap[message.id]) {
            return;
        }

        const counterUrlPrefix = '/messages/' + message.id;
        this.loadingNotesMap[message.id] = true;

        this.annotationsSrv.getCounter(counterUrlPrefix).subscribe({
            next: (counter: Objects.AnnotationsCounter[]) => {
                this.notesCounterMap[message.id] = [...counter];
                this.loadingNotesMap[message.id] = false;
                this.cdr.detectChanges();
            },
            error: (error: HttpErrorResponse) => {
                this.notesCounterMap[message.id] = [];
                this.loadingNotesMap[message.id] = false;
            }
        });
    }

    getNotesForMessage(message: Objects.Message): Objects.AnnotationsCounter[] {
        return message && message.id ? (this.notesCounterMap[message.id] || []) : [];
    }

    onFileMenuClick(menuItem: AmsFilesListItemMenuClick): void {
        switch (menuItem.key) {
            case 'download':
                this.fileDownloadSrv.downloadFile(menuItem.file);
                break;
        }
    }

    onFileIconDownloadClick(file: Objects.AmsFile): void {
        this.fileDownloadSrv.downloadFile(file);
    }

    onNotesSaved(message: Objects.Message, event: any): void {
        event?.stopPropagation?.();
        this.loadNotesForMessage(message);
    }

    onRefreshSignaturesNeeded() {
        this.projectsSrv.loadMessages();
    }

    trackByMessageId(index: number, message: any): string {
        if (!message) {
            return '';
        }

        return message.id ? String(message.id) : String(index);
    }

}
